import type {
  ControlNode as ControlNodeType,
  Port,
} from "../../../../util/src/blueprint/types.js";
import { PortHandle } from "../PortHandle.js";
import dico from "../../../../util/src/language/dico.js";
import BoltIcon from "@mui/icons-material/Bolt";
import React from "react";

export interface EventOutputNodeProps {
  node: ControlNodeType;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
}

/**
 * Terminal node for custom event trigger graphs.
 * Only one `eventOutput` may exist per triggerGraph - all trigger paths
 * must converge here to signal that the custom event has fired.
 */
const EventOutputNodeComponent = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
}: EventOutputNodeProps) => {
  const behaviorDico = dico().behavior;
  const inPort = node.ports.find((port) => port.id === "in");
  if (!inPort) return null;

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 8px",
    fontSize: 12,
    fontWeight: 700,
    color: "#fff",
    background:
      "linear-gradient(180deg, #ff8f8f 0%, #d64545 55%, #a83232 100%)",
  };

  const wrapperStyle: React.CSSProperties = {
    minWidth: 200,
    borderRadius: 8,
    overflow: "hidden",
    background:
      "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(200, 200, 200, 0.98) 100%)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    border: "1px solid #e5e7eb",
  };

  return (
    <div style={wrapperStyle}>
      <div style={headerStyle}>
        <BoltIcon sx={{ fontSize: 14 }} />
        {behaviorDico.controls?.eventOutput ?? "Event output"}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          justifyContent: "flex-start",
          padding: "2px 8px 2px 8px",
        }}
      >
        <PortHandle
          node={node}
          port={inPort}
          setPortsMap={setPortsMap}
          onMouseDown={() => onPortMouseDown?.(node.id, inPort)}
          onMouseUp={() => onPortMouseUp?.(node.id, inPort)}
          isConnected={isPortConnected?.(node.id, inPort.id) ?? false}
        />
      </div>
    </div>
  );
};

export const EventOutputNode = React.memo(EventOutputNodeComponent);
